// HR Dashboard — at-a-glance headcount, attendance, exceptions, payroll status.
// Part of the HR module for Khyati Portal / RSR International.
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { HRC, money, fmtDate, Card, SectionTitle, Stat, Pill, Btn, Loading, Empty } from './hrkit';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
  PieChart, Pie, Legend,
} from 'recharts';

const PAY_LABEL = { monthly: 'Monthly', daily: 'Daily Wage', piece_rate: 'Piece Rate', contractor: 'Contractor' };
const PIE_COLORS = [HRC.navy, HRC.green, HRC.amber, HRC.red, HRC.violet];

const fmtMonth = (m) => {
  if (!m) return '';
  const [y, mo] = String(m).split('-');
  const d = new Date(Number(y), Number(mo) - 1, 1);
  return isNaN(d) ? m : d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};

const ZERO = {
  today: null, month: '', headcount: 0,
  by_department: [], by_pay_type: [],
  today_attendance: { present: 0, absent: 0, on_leave: 0, half_day: 0 },
  today_exceptions: { late: 0, miss: 0, early: 0, absent: 0 },
  pending_leave: 0, pending_corrections: 0,
  active_loans: { count: 0, outstanding: 0 },
  payroll: null,
};

// Small clickable chip for the exceptions card.
const Chip = ({ label, value, color, onClick }) => (
  <div onClick={onClick} style={{
    flex: '1 1 110px', minWidth: 110, cursor: onClick ? 'pointer' : 'default',
    background: color + '14', border: `1px solid ${color}33`, borderRadius: 9, padding: '10px 12px',
  }}>
    <div style={{ fontSize: 22, fontWeight: 800, color }}>{Number(value || 0)}</div>
    <div style={{ fontSize: 11, color: HRC.muted, fontWeight: 600, marginTop: 2 }}>{label}</div>
  </div>
);

export default function HrDashboard(props) {
  const { month, goTo } = props || {};
  const [data, setData] = useState(ZERO);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr('');
    api.get('/hr/dashboard', { params: month ? { month } : {} })
      .then((r) => { if (alive) setData({ ...ZERO, ...(r.data?.data || {}) }); })
      .catch((e) => {
        if (!alive) return;
        setErr(e.response?.data?.message || 'Could not load dashboard');
        setData(ZERO);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [month]);

  if (loading) return <Loading />;

  const att = data.today_attendance || {};
  const exc = data.today_exceptions || {};
  const loans = data.active_loans || {};
  const pendingApprovals = Number(data.pending_leave || 0) + Number(data.pending_corrections || 0);

  const deptData = (data.by_department || []).map((d) => ({ name: d.department || '—', count: Number(d.count || 0) }));
  const payData = (data.by_pay_type || []).map((p) => ({ name: PAY_LABEL[p.type] || p.type, value: Number(p.count || 0) }));

  return (
    <div>
      <SectionTitle right={<span style={{ fontSize: 12, color: HRC.muted }}>{fmtDate(data.today)}</span>}>
        HR Overview — {fmtMonth(month || data.month)}
      </SectionTitle>

      {err && (
        <div style={{ background: HRC.red + '12', color: HRC.red, border: `1px solid ${HRC.red}33`,
          borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{err}</div>
      )}

      {/* KPI tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 14 }}>
        <Stat label="Total Headcount" value={Number(data.headcount || 0)} accent={HRC.navy}
          onClick={goTo ? () => goTo('employees') : undefined} />
        <Stat label="Present Today" value={Number(att.present || 0)} accent={HRC.green} />
        <Stat label="On Leave Today" value={Number(att.on_leave || 0)} sub={Number(att.half_day || 0) ? `${Number(att.half_day)} half-day` : undefined} accent={HRC.amber} />
        <Stat label="Absent Today" value={Number(att.absent || 0)} accent={HRC.red} />
        <Stat label="Pending Approvals" value={pendingApprovals}
          sub={`${Number(data.pending_leave || 0)} leave · ${Number(data.pending_corrections || 0)} corr.`}
          accent={HRC.amber} onClick={goTo ? () => goTo('leave') : undefined} />
        <Stat label="Active Loans" value={Number(loans.count || 0)} sub={money(loans.outstanding)}
          accent={HRC.navy} onClick={goTo ? () => goTo('loans') : undefined} />
      </div>

      {/* Exceptions + Payroll */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: 14, marginTop: 14 }}>
        <Card>
          <SectionTitle right={<Btn variant="ghost" size="sm" onClick={goTo ? () => goTo('exceptions') : undefined}>View all</Btn>}>
            Today's Exceptions
          </SectionTitle>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <Chip label="Late" value={exc.late} color={HRC.amber} onClick={goTo ? () => goTo('exceptions') : undefined} />
            <Chip label="Miss-punch" value={exc.miss} color={HRC.red} onClick={goTo ? () => goTo('exceptions') : undefined} />
            <Chip label="Out Early" value={exc.early} color={HRC.amber} onClick={goTo ? () => goTo('exceptions') : undefined} />
            <Chip label="Absentees" value={exc.absent} color={HRC.red} onClick={goTo ? () => goTo('exceptions') : undefined} />
          </div>
        </Card>

        <Card>
          <SectionTitle>Payroll — {fmtMonth(month || data.month)}</SectionTitle>
          {data.payroll ? (
            <div>
              <div style={{ marginBottom: 12 }}>
                <Pill value={data.payroll.status} />
                <span style={{ fontSize: 12, color: HRC.muted, marginLeft: 8 }}>
                  {Number(data.payroll.employee_count || 0)} employees
                </span>
              </div>
              <div style={{ display: 'flex', gap: 24, marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase' }}>Total Gross</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: HRC.text }}>{money(data.payroll.total_gross)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase' }}>Total Net</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: HRC.green }}>{money(data.payroll.total_net)}</div>
                </div>
              </div>
              <Btn variant="primary" onClick={goTo ? () => goTo('payroll') : undefined}>Open Payroll</Btn>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 13, color: HRC.muted, padding: '12px 0 16px' }}>
                No payroll run for this month yet.
              </div>
              <Btn variant="soft" onClick={goTo ? () => goTo('payroll') : undefined}>Go to Payroll</Btn>
            </div>
          )}
        </Card>
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: 14, marginTop: 14 }}>
        <Card>
          <SectionTitle>Headcount by Department</SectionTitle>
          {deptData.length ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={deptData} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: HRC.muted }} interval={0} angle={deptData.length > 5 ? -20 : 0} textAnchor={deptData.length > 5 ? 'end' : 'middle'} height={deptData.length > 5 ? 50 : 24} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: HRC.muted }} />
                <Tooltip cursor={{ fill: HRC.violet }} />
                <Bar dataKey="count" radius={[5, 5, 0, 0]}>
                  {deptData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty>No department data</Empty>}
        </Card>

        <Card>
          <SectionTitle>Headcount by Pay Type</SectionTitle>
          {payData.some((p) => p.value > 0) ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={payData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={38}>
                  {payData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty>No pay-type data</Empty>}
        </Card>
      </div>
    </div>
  );
}
